import { cidrsOverlap, parseCidr } from './ip';

/**
 * Detecting overlapping networks across an overlay.
 *
 * Two sites that advertise the same or overlapping prefixes into one fabric make
 * routing ambiguous: a packet to 10.0.0.5 could belong to either. The platform's
 * rule is that it never resolves that silently — it does not quietly NAT one side,
 * because a NAT the operator did not ask for is a connectivity mystery waiting to
 * happen. Instead it detects the overlap and refuses to deploy until the operator
 * says how to handle it. This module is the detection half: pure, deterministic,
 * so the same set of advertisements always yields the same conflicts.
 */

/**
 * How an operator resolves an overlap so a fabric can deploy. There is deliberately
 * no "accept and NAT anyway" — the platform never resolves an overlap silently, so
 * every strategy is an explicit, named decision. An overlap with no resolution
 * blocks deployment; renumbering one side removes the overlap and needs no
 * resolution here.
 */
export const OVERLAP_STRATEGIES = [
  /** Exclude the overlapping prefix from one side's advertisement. */
  'SELECTIVE_EXCLUSION',
  /** 1:1 translate one side's overlap to non-conflicting space (explicit NAT). */
  'EXPLICIT_TRANSLATION',
  /** Keep the overlapping networks in separate VRFs so they never collide. */
  'VRF_ISOLATION',
] as const;
export type OverlapStrategy = (typeof OVERLAP_STRATEGIES)[number];

/** One network a site (or device) advertises into the overlay. */
export interface NetworkAdvertisement {
  /** The owner that advertises this prefix — a site or device id. */
  owner: string;
  /** The advertised prefix. */
  cidr: string;
}

/** Two overlapping advertisements from different owners. */
export interface NetworkOverlap {
  a: NetworkAdvertisement;
  b: NetworkAdvertisement;
}

/** A stable key for an overlap, independent of which side is called `a`. Two edges
 * that discover the same overlap from opposite viewpoints derive the same key. */
export function overlapKey(overlap: NetworkOverlap): string {
  const first = `${overlap.a.owner}|${overlap.a.cidr}`;
  const second = `${overlap.b.owner}|${overlap.b.cidr}`;
  const [lo, hi] = first < second ? [first, second] : [second, first];
  return `${lo}::${hi}`;
}

/**
 * Find every pair of advertisements, from different owners, whose prefixes overlap.
 *
 * Same-owner overlaps are not conflicts: a site is entitled to advertise a prefix
 * and a more specific one within it, or to summarize its own networks. Only two
 * different owners claiming overlapping address space is ambiguous. An
 * unparseable prefix is skipped rather than treated as overlapping everything —
 * prefixes are validated where they are entered, so this stays a pure comparison.
 * Output is sorted by owner and prefix so the conflict list is deterministic.
 */
export function findNetworkOverlaps(ads: NetworkAdvertisement[]): NetworkOverlap[] {
  const parsed = ads
    .map((ad) => ({ ad, cidr: parseCidr(ad.cidr) }))
    .filter(
      (p): p is { ad: NetworkAdvertisement; cidr: NonNullable<ReturnType<typeof parseCidr>> } =>
        p.cidr !== null,
    );

  const overlaps: NetworkOverlap[] = [];
  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      const x = parsed[i];
      const y = parsed[j];
      if (!x || !y) continue;
      if (x.ad.owner === y.ad.owner) continue; // a site may overlap its own space
      if (cidrsOverlap(x.cidr, y.cidr)) {
        // Order the pair deterministically so the same conflict always reads the
        // same, whatever order the advertisements came in.
        const [a, b] =
          `${x.ad.owner}|${x.ad.cidr}` <= `${y.ad.owner}|${y.ad.cidr}`
            ? [x.ad, y.ad]
            : [y.ad, x.ad];
        overlaps.push({ a, b });
      }
    }
  }
  overlaps.sort((p, q) => overlapKey(p).localeCompare(overlapKey(q)));
  return overlaps;
}
